import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Slide } from '@mui/material';
import { WifiOff, Sync, CloudDone } from '@mui/icons-material';
import { getQueue, onQueueChange } from '../services/offlineQueue';
import { toast } from 'react-toastify';

const OfflineBanner = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [queueCount, setQueueCount] = useState(getQueue().length);
  const [showSynced, setShowSynced] = useState(false);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);

    const handleSync = (e) => {
      const { synced, failed } = e.detail;
      if (synced > 0) {
        toast.success(`${synced} Änderung${synced > 1 ? 'en' : ''} erfolgreich synchronisiert`);
        setShowSynced(true);
        setTimeout(() => setShowSynced(false), 3000);
      }
      if (failed > 0) {
        toast.error(`${failed} Änderung${failed > 1 ? 'en' : ''} konnten nicht synchronisiert werden`);
      }
    };

    window.addEventListener('offlineSync', handleSync);

    const unsubscribe = onQueueChange((queue) => {
      setQueueCount(queue.length);
    });

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offlineSync', handleSync);
      unsubscribe();
    };
  }, []);

  if (!isOffline && queueCount === 0 && !showSynced) return null;

  return (
    <Slide direction="down" in={isOffline || queueCount > 0 || showSynced}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 0.75,
          px: 2,
          backgroundColor: showSynced ? '#10b981' : isOffline ? '#f59e0b' : '#3b82f6',
          color: '#fff',
          fontSize: '0.85rem',
          fontWeight: 500,
        }}
      >
        {showSynced ? (
          <>
            <CloudDone sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
              Alle Änderungen synchronisiert
            </Typography>
          </>
        ) : isOffline ? (
          <>
            <WifiOff sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
              Offline – Änderungen werden gespeichert
            </Typography>
            {queueCount > 0 && (
              <Chip
                label={`${queueCount} ausstehend`}
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 600, height: 22 }}
              />
            )}
          </>
        ) : queueCount > 0 ? (
          <>
            <Sync sx={{ fontSize: 18, animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
              Synchronisiere {queueCount} Änderung{queueCount > 1 ? 'en' : ''}...
            </Typography>
          </>
        ) : null}
      </Box>
    </Slide>
  );
};

export default OfflineBanner;
